import React, { useState } from 'react'
import Swal from 'sweetalert2'
import { Alert, Badge, Button, Card, CardBody, CustomInput, Table } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBan, faKey, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../../common/Flex'
import Loader from '../../../common/Loader'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import {
  deactivateRewardAccelerator,
  deleteRewardPartner,
  getRewardAccelerators,
  getRewardPartners,
  getRewardsAdminErrorMessage,
  rotateRewardPartnerSecret
} from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'
import { formatDate, formatNumber, formatPercent } from '../../../rewards/rewardsUtils'
import { ACCELERATOR_TYPES } from '../rewardsAdminUtils'
import PartnerModal from './PartnerModal'
import AcceleratorModal from './AcceleratorModal'
import SecretModal from './SecretModal'

// Toda acción destructiva o que invalida algo pide confirmación explícita.
const confirmAction = ({ title, text, confirmButtonText }) =>
  Swal.fire({
    title,
    text,
    type: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#9341af',
    cancelButtonColor: '#bbb',
    confirmButtonText,
    cancelButtonText: 'Cancelar'
  }).then((result) => !!result.value)

const LoadError = ({ message, onRetry }) => (
  <Alert color="danger" className="fs--1 mb-0">
    {message}
    <Button color="link" size="sm" className="ml-1 p-0 align-baseline fs--1" onClick={onRetry}>
      Reintentar
    </Button>
  </Alert>
)

const IconButton = ({ icon, label, onClick, color = 'falcon-default', className = 'ml-1' }) => (
  <Button color={color} size="sm" className={className} onClick={onClick} title={label} aria-label={label}>
    <FontAwesomeIcon icon={icon} />
  </Button>
)

const describeValidity = (accelerator) =>
  accelerator.valid_to
    ? `${formatDate(accelerator.valid_from)} al ${formatDate(accelerator.valid_to)}`
    : `Desde el ${formatDate(accelerator.valid_from)}`

const describeReward = (accelerator) => {
  const tickets = Number(accelerator.raffle_tickets)
  const parts = [tickets === 1 ? '1 ticket' : `${formatNumber(tickets)} tickets`]
  parts.push(accelerator.monthly_cap_per_user === null ? 'sin tope' : `máx. ${formatNumber(accelerator.monthly_cap_per_user)} al mes`)
  return parts.join(' · ')
}

// HO-TASK-289: aliados B2B y el catálogo de aceleradores con los que los
// propietarios ganan Coins fuera del pago de la cuota.
const RewardsPartners = () => {
  const partnersQuery = useSWRFetch('rewards-partners', getRewardPartners)
  const acceleratorsQuery = useSWRFetch('rewards-accelerators', getRewardAccelerators)
  const [partnerModal, setPartnerModal] = useState({ isOpen: false, partner: null })
  const [acceleratorModal, setAcceleratorModal] = useState({ isOpen: false, accelerator: null })
  const [secret, setSecret] = useState(null)
  const [showInactive, setShowInactive] = useState(false)

  const partners = partnersQuery.data?.data || []
  const accelerators = acceleratorsQuery.data?.data || []
  const visibleAccelerators = showInactive ? accelerators : accelerators.filter((item) => item.is_active)
  const hiddenCount = accelerators.length - visibleAccelerators.length

  const refreshAll = () => {
    partnersQuery.mutate()
    acceleratorsQuery.mutate()
  }

  const rotateSecret = async (partner) => {
    const confirmed = await confirmAction({
      title: '¿Generar un secreto nuevo?',
      text: `El secreto actual de ${partner.name} deja de funcionar en el acto: sus eventos se rechazarán hasta que configure el nuevo.`,
      confirmButtonText: 'Sí, generar'
    })
    if (!confirmed) return
    try {
      const { webhook_secret: webhookSecret } = await rotateRewardPartnerSecret(partner.id)
      setSecret({ value: webhookSecret, partnerName: partner.name })
    } catch (error) {
      Toast.fire({ type: 'error', title: getRewardsAdminErrorMessage(error, 'No pudimos generar un secreto nuevo.') })
    }
  }

  const removePartner = async (partner) => {
    const confirmed = await confirmAction({
      title: `¿Eliminar a ${partner.name}?`,
      text: 'El aliado queda inactivo y deja de aparecer en el listado. Sus eventos ya acreditados no cambian.',
      confirmButtonText: 'Sí, eliminar'
    })
    if (!confirmed) return
    try {
      await deleteRewardPartner(partner.id)
      Toast.fire({ type: 'success', title: 'Aliado eliminado.' })
      refreshAll()
    } catch (error) {
      Toast.fire({ type: 'error', title: getRewardsAdminErrorMessage(error, 'No pudimos eliminar el aliado.') })
    }
  }

  const deactivateAccelerator = async (accelerator) => {
    const confirmed = await confirmAction({
      title: `¿Desactivar ${accelerator.name}?`,
      text: 'Los propietarios dejan de ganar Coins con este acelerador. Puedes volver a activarlo editándolo.',
      confirmButtonText: 'Sí, desactivar'
    })
    if (!confirmed) return
    try {
      await deactivateRewardAccelerator(accelerator.id)
      Toast.fire({ type: 'success', title: 'Acelerador desactivado.' })
      acceleratorsQuery.mutate()
    } catch (error) {
      Toast.fire({ type: 'error', title: getRewardsAdminErrorMessage(error, 'No pudimos desactivar el acelerador.') })
    }
  }

  const renderPartners = () => {
    if (partnersQuery.isLoading) return <Loader padding="py-4" />
    if (partnersQuery.error) return <LoadError message="No pudimos cargar los aliados." onRetry={() => partnersQuery.mutate()} />
    if (partners.length === 0) {
      return <p className="fs--1 text-600 text-center py-3 mb-0">Todavía no hay aliados. Crea el primero para cargar sus aceleradores.</p>
    }
    return (
      <Table size="sm" responsive className="fs--1 mb-0">
        <thead className="bg-200 text-900">
          <tr>
            <th>Aliado</th>
            <th className="text-right text-nowrap">Comisión de afiliado</th>
            <th className="text-right">Aceleradores</th>
            <th>Estado</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {partners.map((partner) => (
            <tr key={partner.id}>
              <td>
                <div className="font-weight-semi-bold text-900">{partner.name}</div>
                <div className="fs--2 text-600">{partner.slug}</div>
              </td>
              <td className="text-right">{formatPercent(Number(partner.affiliate_commission_rate) * 100, 2)}</td>
              <td className="text-right">{formatNumber(partner.accelerators_count)}</td>
              <td>
                <Badge color={partner.status === 'active' ? 'soft-success' : 'soft-secondary'} pill>
                  {partner.status === 'active' ? 'Activo' : 'Inactivo'}
                </Badge>
              </td>
              <td className="text-right text-nowrap">
                <IconButton icon={faPen} label={`Editar ${partner.name}`} onClick={() => setPartnerModal({ isOpen: true, partner })} className="" />
                <IconButton icon={faKey} label={`Generar un secreto nuevo para ${partner.name}`} onClick={() => rotateSecret(partner)} />
                <IconButton icon={faTrash} label={`Eliminar ${partner.name}`} onClick={() => removePartner(partner)} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  const renderAccelerators = () => {
    if (acceleratorsQuery.isLoading) return <Loader padding="py-4" />
    if (acceleratorsQuery.error) {
      return <LoadError message="No pudimos cargar los aceleradores." onRetry={() => acceleratorsQuery.mutate()} />
    }
    if (visibleAccelerators.length === 0) {
      return (
        <p className="fs--1 text-600 text-center py-3 mb-0">
          {accelerators.length === 0 ? 'Todavía no hay aceleradores.' : 'No hay aceleradores activos.'}
        </p>
      )
    }
    return (
      <Table size="sm" responsive className="fs--1 mb-0">
        <thead className="bg-200 text-900">
          <tr>
            <th>Acelerador</th>
            <th>Aliado</th>
            <th>Premio por evento</th>
            <th>Vigencia</th>
            <th>Estado</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visibleAccelerators.map((accelerator) => (
            <tr key={accelerator.id}>
              <td style={{ minWidth: 160 }}>
                <div className="font-weight-semi-bold text-900">{accelerator.name}</div>
                <div className="fs--2 text-600">{accelerator.code}</div>
              </td>
              <td>
                <div className="text-900">{accelerator.partner?.name || '—'}</div>
                <div className="fs--2 text-600">{ACCELERATOR_TYPES[accelerator.type] || accelerator.type}</div>
              </td>
              <td className="text-nowrap">
                <div className="font-weight-semi-bold text-900">{formatNumber(accelerator.hc_amount, 2)} HC</div>
                <div className="fs--2 text-600">{describeReward(accelerator)}</div>
              </td>
              <td>{describeValidity(accelerator)}</td>
              <td>
                <Badge color={accelerator.is_active ? 'soft-success' : 'soft-secondary'} pill>
                  {accelerator.is_active ? 'Activo' : 'Inactivo'}
                </Badge>
              </td>
              <td className="text-right text-nowrap">
                <IconButton
                  icon={faPen}
                  label={`Editar ${accelerator.name}`}
                  onClick={() => setAcceleratorModal({ isOpen: true, accelerator })}
                  className=""
                />
                {accelerator.is_active && (
                  <IconButton icon={faBan} label={`Desactivar ${accelerator.name}`} onClick={() => deactivateAccelerator(accelerator)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <Flex justify="between" align="start" className="flex-wrap mb-2">
            <div className="mr-2 mb-2">
              <h5 className="fs-0 mb-1">Aliados</h5>
              <p className="fs--1 text-600 mb-0">Empresas que acreditan Coins a los propietarios por su webhook.</p>
            </div>
            <Button color="primary" size="sm" className="mb-2" onClick={() => setPartnerModal({ isOpen: true, partner: null })}>
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Nuevo aliado
            </Button>
          </Flex>
          {renderPartners()}
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardBody>
          <Flex justify="between" align="start" className="flex-wrap mb-2">
            <div className="mr-2 mb-2">
              <h5 className="fs-0 mb-1">Aceleradores</h5>
              <p className="fs--1 text-600 mb-0">Acciones de los aliados que dan Coins y tickets para el sorteo.</p>
            </div>
            <Flex align="center" className="mb-2">
              <CustomInput
                type="switch"
                id="accelerators-show-inactive"
                label={`Ver inactivos${hiddenCount > 0 ? ` (${hiddenCount})` : ''}`}
                className="fs--1 mr-3"
                checked={showInactive}
                onChange={({ target }) => setShowInactive(target.checked)}
              />
              <Button
                color="primary"
                size="sm"
                onClick={() => setAcceleratorModal({ isOpen: true, accelerator: null })}
                disabled={partners.length === 0}
                title={partners.length === 0 ? 'Primero crea un aliado' : undefined}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Nuevo acelerador
              </Button>
            </Flex>
          </Flex>
          {renderAccelerators()}
        </CardBody>
      </Card>

      <Alert color="secondary" className="fs--1">
        <b>Sorteos:</b> la ejecución del sorteo, la publicación de ganadores y su detalle de auditoría llegarán con el
        motor de sorteo (HO-TASK-273 y HO-TASK-274), que está en el backlog.
      </Alert>

      <PartnerModal
        isOpen={partnerModal.isOpen}
        partner={partnerModal.partner}
        onClose={() => setPartnerModal({ isOpen: false, partner: null })}
        onSaved={(response) => {
          const created = !partnerModal.partner
          setPartnerModal({ isOpen: false, partner: null })
          refreshAll()
          if (created && response?.webhook_secret) {
            setSecret({ value: response.webhook_secret, partnerName: response.data?.name })
          }
        }}
      />
      <AcceleratorModal
        isOpen={acceleratorModal.isOpen}
        accelerator={acceleratorModal.accelerator}
        partners={partners}
        onClose={() => setAcceleratorModal({ isOpen: false, accelerator: null })}
        onSaved={() => {
          setAcceleratorModal({ isOpen: false, accelerator: null })
          refreshAll()
        }}
      />
      <SecretModal secret={secret?.value} partnerName={secret?.partnerName} onClose={() => setSecret(null)} />
    </>
  )
}

export default RewardsPartners
